import { Cross1Icon } from "@radix-ui/react-icons";
import { UsersIcon } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useFocusWithin } from "react-aria";
import { flushSync } from "react-dom";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
  BlockSeparator,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasButton,
  CanvasInput,
  CanvasSimpleSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import { langs } from "@/components/canvas-editor/shapes/lang";
import {
  EntityListShape,
  entityListType,
  inclusionOperators,
  InclusionOperator,
  Dimensions,
  EntityListProps,
  entityTypes,
  EntityType,
  selfLabels,
  inclusionOperatorOptions,
  metadataType,
  entityTypeOptions,
} from "@/components/canvas-editor/shapes/shapes";
import { UserAvatar } from "@/components/canvas-editor/UserAvatar";
import {
  ToolStateNode,
  useIsReadonlyEditor,
} from "@/components/canvas-editor/utils";
import { ListWrapper, ReadOnlyValue } from "@/components/hooks/ListContainer";
import {
  SimpleNodeButton,
  SimpleNodeInput,
} from "@/components/SimpleEditor/SimpleNode";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { ListItem, UnorderedList } from "@/components/ui/typography";

const ROW_HEIGHT = 48;

export function isEntityListShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is EntityListShape {
  return shape.type === entityListType;
}

export class EntityListTool extends ToolStateNode {
  static override id = entityListType;

  static override uiToolDef = (editor: Editor) => ({
    id: entityListType,
    icon: UsersIcon,
    label: EntityListShapeUtil.docs.title,
    kbd: "f",
    defaultColor: EntityListShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(entityListType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={EntityList}
        util={EntityListShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: entityListType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

const defaultDocs =
  "Include or exclude post based on whether they contain a specific entity.";

export class EntityListShapeUtil extends BindableUtil<EntityListShape> {
  static override migrations = createMigrations(entityListType);
  static override type = entityListType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Entity List",
    description: (
      <div>
        <div>{defaultDocs}</div>
        <UnorderedList className="ml-1">
          <ListItem className="my-1">
            <span className="font-black">langs</span> - Filter by language.
          </ListItem>
          <ListItem className="my-1">
            <span className="font-black">urls</span> - Filter for urls.
          </ListItem>
          <ListItem className="my-1">
            <span className="font-black">domains</span> - Filter for url
            domains.
          </ListItem>
          <ListItem className="my-1">
            <span className="font-black">mentions</span> - Filter for mentions
            of user. You can also supply a list/stater pack. When anyone in the
            list is mentioned the post is included.
          </ListItem>
          <ListItem className="my-1">
            <span className="font-black">hashtags</span> - Filter posts with a
            #hashtag
          </ListItem>
        </UnorderedList>
      </div>
    ),
  };

  override getType() {
    return entityListType;
  }

  static override props: RecordProps<EntityListShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    type: T.literalEnum(...entityTypes),
    entities: T.arrayOf(T.string),
    operator: T.literalEnum(...inclusionOperators),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 350,
      minW: 350,
      h: 184,
      entities: [""],
      operator: inclusionOperators[0],
      type: entityTypes[0],
      metadata: undefined,
    };
  }

  override onBeforeCreate(next: EntityListShape): void | EntityListShape {
    const { entities } = next.props;

    if (entities.length > 1) {
      next.props.h += (entities.length - 1) * ROW_HEIGHT;
    }
  }

  override component(shape: EntityListShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <EntityList
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

export function EntityInputRow({
  value,
  type,
  onChange,
  onDelete,
  variant = "canvas",
}: {
  value: string;
  type: "hashtags" | "mentions" | "urls" | "domains";
  onChange: (value: string) => void;
  onDelete?: () => void;
  variant?: "canvas" | "simple";
}) {
  const isReadOnly = useIsReadonlyEditor();
  let placeholder;

  switch (type) {
    case "hashtags":
      placeholder = "hashtag";
      break;
    case "mentions":
      placeholder = "username.bsky.social";
      break;
    case "urls":
      placeholder = "https://example.com/blah";
      break;
    case "domains":
      placeholder = "example.com";
      break;
  }

  const InputComponent = variant === "canvas" ? CanvasInput : SimpleNodeInput;
  const ButtonComponent =
    variant === "canvas" ? CanvasButton : SimpleNodeButton;

  return (
    <div className="relative group flex gap-2 items-center">
      {type === "mentions" && <UserAvatar did={value} />}

      <InputComponent
        aria-label={`${type} entity`}
        value={value}
        placeholder={placeholder}
        className="flex-1"
        onValueChange={onChange}
      />

      {onDelete && !isReadOnly && (
        <ButtonComponent
          size="smIcon"
          variant="ghost"
          className="pointer-events-auto absolute right-1 top-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
          onPress={onDelete}
        >
          <Cross1Icon />
        </ButtonComponent>
      )}
    </div>
  );
}

function ReadOnlyEntityRow({
  value,
  type,
}: {
  value: string;
  type: "hashtags" | "mentions" | "urls" | "domains" | "langs" | "labels";
}) {
  switch (type) {
    case "hashtags":
      return (
        <ReadOnlyValue>
          {value.startsWith("#") ? value : `#${value}`}
        </ReadOnlyValue>
      );
    case "mentions":
      return <ReadOnlyValue>@{value}</ReadOnlyValue>;
    case "urls":
    case "domains":
    case "langs":
    case "labels":
      return <ReadOnlyValue>{value}</ReadOnlyValue>;
  }
}

function EntitySelectRow({
  type,
  value,
  onChange,
  onDelete,
}: {
  type: "langs" | "labels";
  value: string;
  onChange: (value: string) => void;
  onDelete?: () => void;
}) {
  const isReadOnly = useIsReadonlyEditor();

  return (
    <div className="relative group flex gap-2 items-center pointer-events-auto">
      {type === "langs" ? (
        <CanvasSimpleSelect
          aria-label="Language"
          value={value}
          placeholder="Language"
          options={langs}
          className="flex-1"
          onValueChange={onChange}
        />
      ) : (
        <CanvasSimpleSelect
          aria-label="Self Label"
          value={value}
          placeholder="Self Label"
          options={selfLabels}
          className="flex-1"
          onValueChange={onChange}
        />
      )}
      {onDelete && !isReadOnly && (
        <CanvasButton
          size="icon"
          variant="ghost"
          className="pointer-events-auto shrink-0 hidden group-hover:flex"
          onPress={onDelete}
        >
          <Cross1Icon />
        </CanvasButton>
      )}
    </div>
  );
}

function EntityList({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: EntityListShape;
  className?: string;
  updateShape: (
    shape: EntityListShape,
    props: Partial<EntityListProps>,
  ) => void;
}) {
  const variant = shape.props.operator === "in" ? "green" : "red";
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );
  const isReadOnly = useIsReadonlyEditor() || shape.props.isReadOnly;
  const ref = useRef<HTMLDivElement>(null);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: setIsFocusWithin,
  });
  const onAddAnother = useCallback(() => {
    // eslint-disable-next-line @eslint-react/dom/no-flush-sync
    flushSync(() => {
      updateShape(shape, { entities: [...shape.props.entities, ""] });
    });

    if (!ref.current) {
      return;
    }

    const inputs = ref.current.querySelectorAll<HTMLInputElement>("input");

    if (!inputs) {
      return;
    }

    inputs.values().toArray().at(-1)?.focus();
  }, [shape, updateShape]);

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        ref={ref}
        {...focusWithinProps}
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
        onKeyDownCapture={(e) => {
          if (e.key === "Enter" && e.metaKey) {
            e.preventDefault();
            e.stopPropagation();

            onAddAnother();
          }
        }}
      >
        <NodeBlockHeader props={shape.props} docs={EntityListShapeUtil.docs} />
        <BlockBody>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <div className="flex gap-2">
              <CanvasSimpleSelect
                aria-label="Inclusion Operator"
                className="w-28"
                value={shape.props.operator}
                onValueChange={(v) =>
                  updateShape(shape, { operator: v as InclusionOperator })
                }
                options={inclusionOperatorOptions}
              />
              <CanvasSimpleSelect
                aria-label="Entity Type"
                className="flex-1"
                value={shape.props.type}
                onValueChange={(v) =>
                  updateShape(shape, { type: v as EntityType, entities: [""] })
                }
                options={entityTypeOptions}
              />
            </div>
            <BlockSeparator />

            {isReadOnly ? (
              <div className="flex flex-wrap gap-2">
                {shape.props.entities.map((entity, index) => {
                  return (
                    <ReadOnlyEntityRow
                      // eslint-disable-next-line @eslint-react/no-array-index-key
                      key={`${shape.id}-${entity}-${index}`}
                      type={shape.props.type}
                      value={entity}
                    />
                  );
                })}
              </div>
            ) : (
              <>
                <ListWrapper
                  columnCount={shape.props.type === "mentions" ? 1 : undefined}
                >
                  {shape.props.entities.map((entity, index) => {
                    const onChange = (u: string) => {
                      const entities = [...shape.props.entities];
                      entities[index] = u;

                      if (shape.props.type === "mentions") {
                        entities[index] = u.replace(/^@/, "").trim();
                      }

                      updateShape(shape, { entities });
                    };

                    const onDelete = () => {
                      const entities = [...shape.props.entities];
                      entities.splice(index, 1);
                      updateShape(shape, { entities });
                    };

                    if (
                      shape.props.type === "langs" ||
                      shape.props.type === "labels"
                    ) {
                      return (
                        <EntitySelectRow
                          type={shape.props.type}
                          // eslint-disable-next-line @eslint-react/no-array-index-key
                          key={`${shape.id}-entity-member-${index}`}
                          value={entity}
                          onChange={onChange}
                          onDelete={index === 0 ? undefined : onDelete}
                        />
                      );
                    }

                    return (
                      <EntityInputRow
                        // eslint-disable-next-line @eslint-react/no-array-index-key
                        key={`${shape.id}-entity-member-${index}`}
                        type={shape.props.type}
                        value={entity}
                        onChange={onChange}
                        onDelete={index === 0 ? undefined : onDelete}
                      />
                    );
                  })}
                </ListWrapper>
                <CanvasButton
                  size="sm"
                  variant="ghost"
                  onPress={() =>
                    updateShape(shape, {
                      entities: [...shape.props.entities, ""],
                    })
                  }
                >
                  Add Another
                  {isFocusWithin && (
                    <KbdGroup className="hidden md:flex absolute top-1/2 right-2 -translate-y-1/2">
                      <Kbd>Meta + ⏎</Kbd>
                    </KbdGroup>
                  )}
                </CanvasButton>
              </>
            )}
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
